import { useEffect, useLayoutEffect, lazy, Suspense } from 'react';
import { jobAgentVisible } from './jobAgent/launch';
import { courseRoutes } from './courseBuilder/routes';
import { ebookRoutes } from './ebookBuilder/routes';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PostHogProvider, PostHogErrorBoundary } from 'posthog-js/react';
import posthog, { surface as analyticsSurface } from './analytics';
import { ThemeProvider } from './contexts/ThemeContext';
import UpdateModal from './components/UpdateModal';
import DeepLinkListener from './components/DeepLinkListener';
import DeviceReporter from './components/DeviceReporter';
import AnalyticsTracker from './components/AnalyticsTracker';
import { UpdateProvider } from './contexts/UpdateContext';
import { RecruiterAuthProvider, useRecruiterAuth } from './contexts/RecruiterAuthContext';
import RecruiterLayout from './components/RecruiterLayout';
import MemberGate from './components/MemberGate';
import { CASE_STUDIES } from './pages/libraries';

const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const MyLearning = lazy(() => import('./pages/MyLearning'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Posts = lazy(() => import('./pages/Posts'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const Download = lazy(() => import('./pages/Download'));
const DownloadGuide = lazy(() => import('./pages/DownloadGuide'));
const Support = lazy(() => import('./pages/Support'));
const ResumeBuilder = lazy(() => import('./pages/ResumeBuilder'));
const ResumeEditor = lazy(() => import('./pages/ResumeEditor'));
const ResumePreview = lazy(() => import('./pages/ResumePreview'));
const ResumeAIGenerate = lazy(() => import('./pages/ResumeAIGenerate'));
const CoverLetterBuilder = lazy(() => import('./pages/CoverLetterBuilder'));
const CoverLetterEditor = lazy(() => import('./pages/CoverLetterEditor'));
const CoverLetterAIGenerate = lazy(() => import('./pages/CoverLetterAIGenerate'));
const Profile = lazy(() => import('./pages/Profile'));
const InterviewQuestions = lazy(() => import('./pages/InterviewQuestions'));
const VirtualInterviewer = lazy(() => import('./pages/VirtualInterviewer'));
const JobSearchTracker = lazy(() => import('./pages/JobSearchTracker'));
const JobAgent = lazy(() => import('./pages/JobAgent'));
const CertExamPrep = lazy(() => import('./pages/CertExamPrep'));
const Membership = lazy(() => import('./pages/Membership'));
const Referral = lazy(() => import('./pages/Referral'));
const JobListings = lazy(() => import('./pages/JobListings'));
const LearningPath = lazy(() => import('./pages/LearningPath'));
const Projects = lazy(() => import('./pages/Projects'));
const Labs = lazy(() => import('./pages/Labs'));
const Courses = lazy(() => import('./pages/Courses'));
const CourseDetail = lazy(() => import('./pages/CourseDetail'));
const CoursePlayer = lazy(() => import('./pages/CoursePlayer'));
const AiCourseWizard = lazy(() => import('./pages/AiCourseWizard'));
const AiCourseViewer = lazy(() => import('./pages/AiCourseViewer'));
const AiCourseBuild = lazy(() => import('./pages/AiCourseBuild'));
const AiCourseEditor = lazy(() => import('./pages/AiCourseEditor'));
const Ebooks = lazy(() => import('./pages/Ebooks'));
const AiEbookWizard = lazy(() => import('./pages/AiEbookWizard'));
const AiEbookReader = lazy(() => import('./pages/AiEbookReader'));
const AiEbookBuild = lazy(() => import('./pages/AiEbookBuild'));
const AiEbookEditor = lazy(() => import('./pages/AiEbookEditor'));
const LabsCategory = lazy(() => import('./pages/LabsCategory'));
const LabsNotes = lazy(() => import('./pages/LabsNotes'));
const LabDetail = lazy(() => import('./pages/LabDetail'));
const AutomationLabs = lazy(() => import('./pages/AutomationLabs'));
const AutomationLabDetail = lazy(() => import('./pages/AutomationLabDetail'));
const KubernetesLabs = lazy(() => import('./pages/KubernetesLabs'));
const KubernetesLabDetail = lazy(() => import('./pages/KubernetesLabDetail'));
const AiProvider = lazy(() => import('./pages/AiProvider'));
const SystemDesigns = lazy(() => import('./pages/SystemDesigns'));
const SystemDesignDetail = lazy(() => import('./pages/SystemDesignDetail'));
const SystemDesignGenerator = lazy(() => import('./pages/SystemDesignGenerator'));
const CareerRoadmaps = lazy(() => import('./pages/CareerRoadmaps'));
const CareerRoadmapDetail = lazy(() => import('./pages/CareerRoadmapDetail'));
const CareerRoadmapGenerator = lazy(() => import('./pages/CareerRoadmapGenerator'));
const LocalCloudLabs = lazy(() => import('./pages/LocalCloudLabs'));
const LocalCloudLabDetail = lazy(() => import('./pages/LocalCloudLabDetail'));
const AgentLabs = lazy(() => import('./pages/AgentLabs'));
const Shadow = lazy(() => import('./pages/Shadow'));
const AgentLabDetail = lazy(() => import('./pages/AgentLabDetail'));
const SocialProShare = lazy(() => import('./pages/SocialProShare'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Certificates = lazy(() => import('./pages/Certificates'));
const CertificateView = lazy(() => import('./pages/CertificateView'));
const Teams = lazy(() => import('./pages/Teams'));
const TeamJoin = lazy(() => import('./pages/TeamJoin'));
const TeamPlans = lazy(() => import('./pages/TeamPlans'));
const Sponsor = lazy(() => import('./pages/Sponsor'));
const TagArchive = lazy(() => import('./pages/TagArchive'));
const SsoCallback = lazy(() => import('./pages/SsoCallback'));
const GithubCallback = lazy(() => import('./pages/GithubCallback'));
const LinkedinCallback = lazy(() => import('./pages/LinkedinCallback'));
const SocialPostDetail = lazy(() => import('./pages/SocialPostDetail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Coach = lazy(() => import('./pages/Coach'));
const DesktopAuthHandoff = lazy(() => import('./pages/DesktopAuthHandoff'));
const RecruiterLogin = lazy(() => import('./pages/RecruiterLogin'));
const RecruiterSignup = lazy(() => import('./pages/RecruiterSignup'));
const RecruiterDashboard = lazy(() => import('./pages/RecruiterDashboard'));
const RecruiterJobs = lazy(() => import('./pages/RecruiterJobs'));
const RecruiterApplicants = lazy(() => import('./pages/RecruiterApplicants'));
const RecruiterProfile = lazy(() => import('./pages/RecruiterProfile'));
const RecruiterChangePassword = lazy(() => import('./pages/RecruiterChangePassword'));
const Hiring = lazy(() => import('./pages/Hiring'));

// Use HashRouter for Electron (file://) and Capacitor (capacitor://) — both break with BrowserRouter
const isFileProtocol  = typeof window !== 'undefined' && window.location.protocol === 'file:';
const isCapacitor     = typeof window !== 'undefined' && !!window.Capacitor;
const Router = (isFileProtocol || isCapacitor) ? HashRouter : BrowserRouter;

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  // An in-page wall rather than a bounce to /login: it keeps the navigation,
  // names what is behind the door, and signs them in without a second hop.
  if (!user) return <MemberGate />;
  return children;
}

function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  if (loading) return <PageLoader />;
  if (user) {
    const isElectron = typeof window !== 'undefined' && !!window.electronEnv;
    const next = params.get('redirect') || (isElectron ? '/dashboard' : '/posts');
    return <Navigate to={next} replace />;
  }
  return children;
}

// Recruiters are a separate auth world (their own token + stack). These guards keep
// the recruiter hub fully isolated from the member app.
function RecruiterRoute({ children }) {
  const { recruiter, loading } = useRecruiterAuth();
  if (loading) return <PageLoader />;
  if (!recruiter) return <Navigate to="/recruiter/login" replace />;
  return <RecruiterLayout>{children}</RecruiterLayout>;
}

function RecruiterGuestRoute({ children }) {
  const { recruiter, loading } = useRecruiterAuth();
  if (loading) return <PageLoader />;
  if (recruiter) return <Navigate to="/recruiter/dashboard" replace />;
  return children;
}

function PageLoader() {
  return (
    <div className="min-h-screen bg-lx-bg flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-500 text-sm">Loading LearnXops…</p>
      </div>
    </div>
  );
}

// Per-route document titles. Longest matching path prefix wins. Dynamic content
// pages (e.g. post detail) override this with the actual item title.
const ROUTE_TITLES = {
  '/dashboard': 'Dashboard',
  '/my-learning': 'My Learning',
  '/coach': 'AI Coach',
  '/posts': 'Articles',
  '/sponsor': 'Sponsor the Newsletter',
  '/system-designs': 'System Designs',
  '/system-designs/generate': 'AI System Design Generator',
  '/case-studies': 'Case Studies',
  '/career-roadmaps': 'Career Roadmaps',
  '/career-roadmaps/generate': 'AI Career Roadmap Generator',
  '/resume-builder': 'Resume Builder',
  '/cover-letter': 'Cover Letter Builder',
  '/interview': 'Interview Q&A',
  '/virtual-interview': 'Mock Interview',
  '/job-tracker': 'Job Search Tracker',
  '/job-agent': 'Job Agent',
  '/cert-exam': 'Cert Exam Prep',
  '/jobs': 'Jobs',
  '/hiring': 'Hiring on LearnXops',
  '/courses': 'Courses',
  '/courses/new': 'Build a course with AI',
  '/ebooks': 'Ebooks',
  '/ebooks/new': 'Build an ebook with AI',
  '/labs': 'Labs',
  '/labs/kubernetes': 'Kubernetes Labs',
  '/labs/local-cloud': 'Local Cloud Labs',
  '/labs/ai-agents': 'AI Agent Labs',
  '/settings/ai-provider': 'AI Provider',
  '/labs/automation': 'Automation Labs',
  '/labs/notes': 'Lab Notes',
  '/learning-paths': 'Learning Paths',
  '/projects': 'Projects',
  '/leaderboard': 'Leaderboard',
  '/certificates': 'Certificates',
  '/teams': 'Team Insights',
  '/social': 'Social',
  '/profile': 'Profile',
  '/membership': 'Membership',
  '/referral': 'Refer & Earn',
  '/support': 'Support',
  '/download': 'Download',
  '/login': 'Sign in',
  '/signup': 'Sign up',
  '/recruiter/login': 'Recruiter sign in',
  '/recruiter/signup': 'Become a recruiter',
  '/recruiter/dashboard': 'Recruiter Dashboard',
  '/recruiter/jobs': 'My Jobs · Recruiter',
  '/recruiter/profile': 'Recruiter Profile',
  '/recruiter/password': 'Change Password · Recruiter',
};

function RouteTitle() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    let best = '';
    for (const p of Object.keys(ROUTE_TITLES)) {
      if ((path === p || path.startsWith(p + '/')) && p.length > best.length) best = p;
    }
    const title = ROUTE_TITLES[best];
    // Content pages (post/lab detail) set their own title after data loads.
    document.title = title ? `${title} · LearnXops` : 'LearnXops';
  }, [location.pathname]);
  return null;
}

/*
 * Whether a route has ever finished rendering in this page load.
 *
 * It decides what Suspense shows. Before the first route commits the document
 * is empty — the prerendered markup has already been removed by its own
 * inline script — so a placeholder there would be the only thing painted, and
 * the real content arriving would displace it. That is a layout shift against
 * nothing. Rendering nothing instead means the first thing painted is the
 * page itself.
 *
 * Afterwards a navigation genuinely needs feedback, and by then a shift is
 * attributed to the click that caused it.
 */
let routeHasRendered = false;

function MarkRouteRendered() {
  useLayoutEffect(() => { routeHasRendered = true; }, []);
  return null;
}

function RouteFallback() {
  if (!routeHasRendered) return null;
  return (
    <div style={{ minHeight: '100vh', background: 'var(--lx-bg, #0a0f1e)' }} aria-busy="true">
      <div style={{
        width: 22, height: 22, margin: '38vh auto 0',
        border: '2px solid rgba(139,92,246,0.25)', borderTopColor: '#8b5cf6',
        borderRadius: '50%', animation: 'lxspin .7s linear infinite',
      }} />
      <style>{'@keyframes lxspin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

function RootRedirect() {
  const isElectron = typeof window !== 'undefined' && !!window.electronEnv;
  return <Navigate to={isElectron ? '/dashboard' : '/posts'} replace />;
}

// Shown when a render crash reaches the boundary. PostHog has already captured
// the exception by the time this renders.
function AppCrashFallback() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--lx-bg, #0a0f1e)', color: '#e5e7eb', padding: 24, textAlign: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{ maxWidth: 420 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <h1 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>Something went wrong</h1>
        <p style={{ fontSize: 14, color: '#9ca3af', lineHeight: 1.6, margin: '0 0 20px' }}>
          The error has been reported. Reloading usually fixes it.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 22px', borderRadius: 12, border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg,#6366f1,#7c3aed)', color: '#fff',
            fontSize: 14, fontWeight: 600,
          }}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <PostHogProvider client={posthog}>
    <PostHogErrorBoundary
      additionalProperties={(error, errorInfo) => ({
        component_stack: errorInfo?.componentStack,
        surface: analyticsSurface(),
      })}
      fallback={<AppCrashFallback />}
    >
    <AuthProvider>
      <RecruiterAuthProvider>
      <ThemeProvider>
        <UpdateProvider>
        <UpdateModal />
        <Router>
          <RouteTitle />
          <AnalyticsTracker />
          <DeviceReporter />
          <DeepLinkListener />
          <Suspense fallback={<RouteFallback />}>
          <MarkRouteRendered />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/desktop-auth" element={<DesktopAuthHandoff />} />
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/my-learning" element={<PrivateRoute><MyLearning /></PrivateRoute>} />
            <Route path="/coach"     element={<PrivateRoute><Coach /></PrivateRoute>} />
            <Route path="/login"  element={<GuestRoute><Login /></GuestRoute>} />
            <Route path="/signup" element={<GuestRoute><Signup /></GuestRoute>} />

            {/* ── Recruiter hub (isolated auth + stack) ── */}
            <Route path="/recruiter" element={<Navigate to="/recruiter/dashboard" replace />} />
            <Route path="/recruiter/login"  element={<RecruiterGuestRoute><RecruiterLogin /></RecruiterGuestRoute>} />
            <Route path="/recruiter/signup" element={<RecruiterGuestRoute><RecruiterSignup /></RecruiterGuestRoute>} />
            <Route path="/recruiter/dashboard" element={<RecruiterRoute><RecruiterDashboard /></RecruiterRoute>} />
            <Route path="/recruiter/jobs" element={<RecruiterRoute><RecruiterJobs /></RecruiterRoute>} />
            <Route path="/recruiter/jobs/:jobId/applicants" element={<RecruiterRoute><RecruiterApplicants /></RecruiterRoute>} />
            <Route path="/recruiter/profile" element={<RecruiterRoute><RecruiterProfile /></RecruiterRoute>} />
            <Route path="/recruiter/password" element={<RecruiterRoute><RecruiterChangePassword /></RecruiterRoute>} />

            {/* Member-facing Hiring page (internal recruiter postings) */}
            <Route path="/hiring" element={<PrivateRoute><Hiring /></PrivateRoute>} />
            <Route path="/verify" element={<VerifyEmail />} />
            <Route path="/download" element={<Download />} />
            <Route path="/download/:platform" element={<DownloadGuide />} />
            <Route path="/support" element={<Support />} />
            <Route path="/posts"         element={<Posts />} />
            {/* Public: the catalogue and every curated design are open; the API gates the article body. */}
            <Route path="/system-designs" element={<SystemDesigns />} />
            <Route path="/system-designs/page/:page" element={<SystemDesigns />} />
            {/* Category views. Additive: /system-designs and /system-designs/:slug
                are untouched, and React Router ranks the static "category"
                segment above the :slug wildcard, so no existing URL changes. */}
            <Route path="/system-designs/category/:category" element={<SystemDesigns />} />
            <Route path="/system-designs/category/:category/page/:page" element={<SystemDesigns />} />
            <Route path="/system-designs/generate" element={<PrivateRoute><SystemDesignGenerator /></PrivateRoute>} />
            {/* Public: signed-out visitors get a teaser + sign-in gate, served truncated by the API. */}
            <Route path="/system-designs/:slug" element={<SystemDesignDetail />} />
            {/* Case studies: the same pages as the system designs, pointed at the
                case-study library (src/pages/libraries.js). Public; the API gates the body. */}
            <Route path="/case-studies" element={<SystemDesigns library={CASE_STUDIES} />} />
            <Route path="/case-studies/page/:page" element={<SystemDesigns library={CASE_STUDIES} />} />
            <Route path="/case-studies/category/:category" element={<SystemDesigns library={CASE_STUDIES} />} />
            <Route path="/case-studies/category/:category/page/:page" element={<SystemDesigns library={CASE_STUDIES} />} />
            <Route path="/case-studies/:slug" element={<SystemDesignDetail library={CASE_STUDIES} />} />
            {/* Public, like the system designs: the API serves a teaser and gates the body. */}
            <Route path="/career-roadmaps" element={<CareerRoadmaps />} />
            <Route path="/career-roadmaps/page/:page" element={<CareerRoadmaps />} />
            <Route path="/career-roadmaps/category/:category" element={<CareerRoadmaps />} />
            <Route path="/career-roadmaps/category/:category/page/:page" element={<CareerRoadmaps />} />
            <Route path="/career-roadmaps/generate" element={<PrivateRoute><CareerRoadmapGenerator /></PrivateRoute>} />
            <Route path="/career-roadmaps/:slug" element={<CareerRoadmapDetail />} />
            {/* Public: signed-out visitors see a preview + inline sign-in gate (no login redirect) */}
            <Route path="/posts/:postId" element={<PostDetail />} />
            <Route path="/resume-builder" element={<PrivateRoute><ResumeBuilder /></PrivateRoute>} />
            <Route path="/resume-builder/new" element={<PrivateRoute><ResumeEditor /></PrivateRoute>} />
            <Route path="/resume-builder/edit/:resumeId" element={<PrivateRoute><ResumeEditor /></PrivateRoute>} />
            <Route path="/resume-builder/preview/:resumeId" element={<PrivateRoute><ResumePreview /></PrivateRoute>} />
            <Route path="/resume-builder/ai-generate" element={<PrivateRoute><ResumeAIGenerate /></PrivateRoute>} />
            <Route path="/cover-letter" element={<PrivateRoute><CoverLetterBuilder /></PrivateRoute>} />
            <Route path="/cover-letter/new" element={<PrivateRoute><CoverLetterEditor /></PrivateRoute>} />
            <Route path="/cover-letter/edit/:id" element={<PrivateRoute><CoverLetterEditor /></PrivateRoute>} />
            <Route path="/cover-letter/ai-generate" element={<PrivateRoute><CoverLetterAIGenerate /></PrivateRoute>} />
            <Route path="/interview" element={<Navigate to="/interview/browse" replace />} />
            <Route path="/interview/:tab" element={<PrivateRoute><InterviewQuestions /></PrivateRoute>} />
            <Route path="/virtual-interview" element={<PrivateRoute><VirtualInterviewer /></PrivateRoute>} />
            <Route path="/job-tracker" element={<PrivateRoute><JobSearchTracker /></PrivateRoute>} />
            <Route path="/job-agent" element={jobAgentVisible() ? <PrivateRoute><JobAgent /></PrivateRoute> : <Navigate to="/jobs" replace />} />
            <Route path="/cert-exam" element={<PrivateRoute><CertExamPrep /></PrivateRoute>} />
            <Route path="/shadow" element={<Shadow />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/ai-project-idea-generator" element={<PrivateRoute><Projects /></PrivateRoute>} />
            <Route path="/learning-paths" element={<Navigate to="/learning-paths/ai" replace />} />
            {/* The AI roadmap generator is a per-member tool with a quota, so it
                stays behind the gate. The curated paths are a reading list over
                published articles and are open to everyone — React Router ranks
                the static "roadmap" segment above the :pathSlug wildcard. */}
            <Route path="/learning-paths/roadmap" element={<PrivateRoute><LearningPath /></PrivateRoute>} />
            <Route path="/learning-paths/:pathSlug" element={<LearningPath />} />
            <Route path="/jobs" element={<PrivateRoute><JobListings /></PrivateRoute>} />
            {/* /courses is a public hub now (its LMS, mine and shared tabs ask
                visitors to sign in). AI courses and LMS courses share
                /courses/:courseId; the old /courses/ai/… addresses redirect —
                see courseBuilder/routes.js. */}
            {courseRoutes({
              Private: PrivateRoute,
              pages: {
                Courses, CourseDetail, CoursePlayer,
                Wizard: AiCourseWizard, Viewer: AiCourseViewer, Build: AiCourseBuild, Editor: AiCourseEditor,
              },
            })}
            {/* The AI Ebook Builder: /ebooks (hub) and /ebooks/… — see ebookBuilder/routes.js. */}
            {ebookRoutes({
              Private: PrivateRoute,
              pages: { Ebooks, Wizard: AiEbookWizard, Reader: AiEbookReader, Build: AiEbookBuild, Editor: AiEbookEditor },
            })}
            <Route path="/labs" element={<PrivateRoute><Labs /></PrivateRoute>} />
            <Route path="/labs/automation" element={<PrivateRoute><AutomationLabs /></PrivateRoute>} />
            <Route path="/labs/automation/:challengeId" element={<PrivateRoute><AutomationLabDetail /></PrivateRoute>} />
            <Route path="/labs/kubernetes" element={<PrivateRoute><KubernetesLabs /></PrivateRoute>} />
            <Route path="/labs/kubernetes/:challengeId" element={<PrivateRoute><KubernetesLabDetail /></PrivateRoute>} />
            <Route path="/settings/ai-provider" element={<PrivateRoute><AiProvider /></PrivateRoute>} />
            <Route path="/settings/copilot/callback" element={<PrivateRoute><AiProvider /></PrivateRoute>} />
            <Route path="/labs/local-cloud" element={<LocalCloudLabs />} />
            <Route path="/labs/local-cloud/:challengeId" element={<PrivateRoute><LocalCloudLabDetail /></PrivateRoute>} />
            <Route path="/labs/ai-agents" element={<PrivateRoute><AgentLabs /></PrivateRoute>} />
            <Route path="/labs/ai-agents/:challengeId" element={<PrivateRoute><AgentLabDetail /></PrivateRoute>} />
            <Route path="/labs/notes" element={<PrivateRoute><LabsNotes /></PrivateRoute>} />
            <Route path="/labs/category/:category" element={<PrivateRoute><LabsCategory /></PrivateRoute>} />
            <Route path="/labs/:challengeId" element={<PrivateRoute><LabDetail /></PrivateRoute>} />
            <Route path="/u/:userId" element={<PrivateRoute><PublicProfile /></PrivateRoute>} />
            <Route path="/social/post/:postId" element={<PrivateRoute><SocialPostDetail /></PrivateRoute>} />
            <Route path="/social" element={<Navigate to="/social/feed" replace />} />
            <Route path="/social/:tab" element={<PrivateRoute><SocialProShare /></PrivateRoute>} />
            <Route path="/leaderboard" element={<PrivateRoute><Leaderboard /></PrivateRoute>} />
            <Route path="/certificates" element={<PrivateRoute><Certificates /></PrivateRoute>} />
            <Route path="/certificate/:certId" element={<CertificateView />} />
            <Route path="/teams-plans" element={<TeamPlans />} />
            <Route path="/sponsor" element={<Sponsor />} />
            {/* Long-crawled Ghost archive URLs on this host. They render for
                real now instead of an empty shell, and canonical to www where
                the same archives are published. */}
            <Route path="/tag/:slug" element={<TagArchive kind="tag" />} />
            <Route path="/tag/:slug/page/:page" element={<TagArchive kind="tag" />} />
            <Route path="/author/:slug" element={<TagArchive kind="author" />} />
            <Route path="/author/:slug/page/:page" element={<TagArchive kind="author" />} />
            <Route path="/sso/callback" element={<SsoCallback />} />
            <Route path="/auth/github/callback" element={<GithubCallback />} />
            <Route path="/auth/linkedin/callback" element={<LinkedinCallback />} />
            <Route path="/teams" element={<PrivateRoute><Teams /></PrivateRoute>} />
            <Route path="/teams/join/:token" element={<PrivateRoute><TeamJoin /></PrivateRoute>} />
            <Route path="/referral" element={<PrivateRoute><Referral /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/:slug"         element={<PostDetail />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
        </Router>
        </UpdateProvider>
      </ThemeProvider>
      </RecruiterAuthProvider>
    </AuthProvider>
    </PostHogErrorBoundary>
    </PostHogProvider>
  );
}
